import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Button } from './ui/button'

interface GameResultOverlayProps {
  result: 'win' | 'lose'
  onReturnToMenu: () => void
  selectedTheme: 'FANTASY' | 'NOIR' | 'SUNSET'
}

export function GameResultOverlay({ result, onReturnToMenu, selectedTheme }: GameResultOverlayProps) {
  const isWin = result === 'win'

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-md overflow-hidden"
    >
      {/* Background Cinematic Effects */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {isWin ? (
          <div className="absolute inset-0 bg-emerald-500/10 animate-pulse transition-all duration-[3000ms]" />
        ) : (
          <div className="absolute inset-0 bg-red-950/20 animate-pulse transition-all duration-[3000ms]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black" />
      </div>

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ 
          type: "spring",
          damping: 25,
          stiffness: 120,
          delay: 0.2
        }}
        className="relative z-10 flex flex-col items-center text-center max-w-lg px-8 py-16"
      >
        <motion.div
           initial={{ opacity: 0, y: -20 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: 0.4 }}
           className="mb-8"
        >
          <span className={cn(
            "text-[10px] font-black uppercase tracking-[1em] mb-4 block opacity-60 ml-2",
            isWin ? "text-emerald-400" : "text-red-400"
          )}>
            The match has concluded
          </span>
          <h2 className={cn(
            "text-8xl font-action font-black tracking-tighter italic leading-none drop-shadow-[0_0_30px_rgba(255,255,255,0.1)]",
            isWin ? "text-emerald-400" : "text-red-500"
          )}>
            {isWin ? 'VICTORY' : 'DEFEAT'}
          </h2>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-white/60 text-sm font-medium tracking-wide mb-12 max-w-xs leading-relaxed"
        >
          {isWin 
            ? "Your tactical brilliance has carved a new legend in the annals of history." 
            : "Even the sharpest blade can dull. Retreat, regroup, and return stronger."
          }
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="w-full flex flex-col gap-3"
        >
          <Button
            onClick={onReturnToMenu}
            className={cn(
              "h-14 w-full text-[11px] font-black uppercase tracking-[0.4em] transition-all duration-300",
              isWin 
                ? "bg-emerald-500 hover:bg-emerald-400 text-black shadow-xl shadow-emerald-500/20" 
                : "bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm border border-white/10"
            )}
          >
            Return to Menu
          </Button>
          
          <p className="text-[9px] font-black text-white/20 uppercase tracking-[0.2em] mt-4">
            Character Development © 2026
          </p>
        </motion.div>
      </motion.div>
      
      {/* Decorative lines */}
      <div className="absolute top-1/2 left-0 w-full h-[1px] bg-white/5 -translate-y-1/2 z-0" />
      <div className="absolute top-0 left-1/2 w-[1px] h-full bg-white/5 -translate-x-1/2 z-0" />
    </motion.div>
  )
}
